import {
  ChangeEvent,
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useState,
} from "react";
import "./ExerciseDialog.css";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import Dropdown from "components/Dropdown/Dropdown";
import ArrowRightIcon from "images/arrow_right.svg?react";
import CorrectIcon from "images/correct.svg?react";
import InCorrectIcon from "images/incorrect.svg?react";
import CheckOutlineIcon from "images/check_circle_outline.svg?react";
import ErrorOutlineIcon from "images/cancel.svg?react";
import VisibilityIcon from "images/visibility.svg?react";
import VisibilityOffIcon from "images/visibility_off.svg?react";
import CheckIcon from "@mui/icons-material/DoneRounded";
import TranslateIcon from "images/translate.svg?react";
import { fetchTranslation } from "services/translate";
import ForbiddenImg from "images/403.svg?react";
import ProgressRing from "components/ui/ProgressRing";
import { useTranslation } from "react-i18next";
import {
  aiExercisesQuery,
  createAiExercises,
  submitAiExerciseAnswers,
} from "services/talkToTally/exercises";
import { useUser } from "contexts/UserContext";
import LanguageSelect from "components/LanguageSelect/LanguageSelect";
import { Language } from "services/config";

import { useMutation, useQuery } from "@tanstack/react-query";
import Dialog from "components/ui/Dialog";
import { GhostButton } from "components/adminUi/Button";
import { VStack } from "components/Stack";
import Button from "components/ui/Button";
import ApiErrorBanner from "components/ApiErrorBanner";

type Props = {
  smallVocab: boolean;
  open: boolean;
  onClose: () => void;
  explanation: string;
};

/** A "passing" quiz result: the celebratory check only appears from here up —
 * below it the score presents neutrally (no triumphant check beside "0%"). */
const PASS_THRESHOLD = 70;

export const ExerciseDialog = (props: Props) => {
  const user = useUser();

  const { mutate: generateExercises, ...createAiExercisesMut } = useMutation({
    mutationFn: createAiExercises,
  });
  const exerciseSequenceId = createAiExercisesMut.data?.id;
  const exercisesResult = useQuery({
    ...aiExercisesQuery({ id: exerciseSequenceId! }),
    enabled: !!exerciseSequenceId,
    retry: failureCount => failureCount < 10,
    retryDelay: 3000,
  });
  const exercises = exercisesResult.data?.sentences;

  const submitAiExerciseAnswersMut = useMutation({
    mutationFn: submitAiExerciseAnswers,
  });

  const [step, setStep] = useState<number>(0);
  const [stepAnswers, setStepAnswers] = useState<{ value: string; isCorrect: boolean }[]>(
    []
  );
  const [stepSubmitted, setStepSubmitted] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [translatedText, setTranslatedText] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [targetLang, setTargetLang] = useState(user.profile.native_language);
  const [showLimitError, setShowLimitError] = useState(false);
  const { t } = useTranslation();

  const stepAnswer = exercises?.[step]?.answers.find(
    answer => answer.id === stepAnswers[step]?.value
  );
  const correctAnswer = exercises?.[step]?.answers.find(answer => answer.is_correct);

  const exerciseCount = exercises ? exercises.length : 0;
  const percentage = Math.floor(
    (stepAnswers.filter(answer => answer.isCorrect).length / exerciseCount) * 100
  );
  const passed = percentage >= PASS_THRESHOLD;

  useEffect(() => {
    if (!props.smallVocab) {
      generateExercises(undefined);
    }
  }, [props.smallVocab, generateExercises]);

  const handleTranslateExercise = (lang: string) => {
    const explanation = stepAnswer?.explanation;
    if (!explanation) {
      return;
    }
    fetchTranslation({
      target_lang: lang,
      source_lang: "en",
      text: explanation,
    }).then(
      resp => {
        setTranslatedText(resp.translated_text);
      },
      (err: unknown) => {
        setError(err);
      }
    );
  };

  const handleClickTranslate = (e: ReactMouseEvent<HTMLElement>) => {
    if (user.profile.native_language) {
      handleTranslateExercise(user.profile.native_language);
    } else {
      if (translatedText) {
        setTranslatedText("");
        e.stopPropagation();
      }
    }
  };

  const handlePracticeMore = () => {
    setShowLimitError(false);
    setStepAnswers([]);
    setStep(0);
    setStepSubmitted(false);
    setShowResult(false);
    setShowAnswer(false);
    setTranslatedText("");
    setError(null);
    generateExercises(undefined);
  };

  const handleSubmitAnswer = () => {
    if (stepSubmitted) {
      if (step === exerciseCount - 1) {
        setShowResult(true);
      } else {
        setStep(step + 1);
        setStepSubmitted(false);
        setShowAnswer(false);
        setTranslatedText("");
      }
    } else if (stepAnswer) {
      setStepSubmitted(true);
      submitAiExerciseAnswersMut.mutate({
        sequenceId: exerciseSequenceId!,
        exerciseId: exercises![step].id,
        answerId: stepAnswer.id,
      });
    }
  };

  const handleChangeAnswer = (e: ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value) {
      const selectedAnswer = exercises?.[step]?.answers.find(
        answer => answer.id === e.target.value
      );

      if (stepAnswers[step]) {
        setStepAnswers(
          stepAnswers.map((answer, index) =>
            index !== step
              ? answer
              : {
                  value: e.target.value,
                  isCorrect: !!selectedAnswer?.is_correct,
                }
          )
        );
      } else {
        setStepAnswers([
          ...stepAnswers,
          { value: e.target.value, isCorrect: !!selectedAnswer?.is_correct },
        ]);
      }
    }
  };

  const handleToggleShowAnswer = () => {
    setShowAnswer(!showAnswer);
  };

  const handleChangeTargetLang = (language: Language | undefined) => {
    setTargetLang(language?.code ?? null);
    if (language) {
      handleTranslateExercise(language.code);
    }
  };

  const showAnswerToggle =
    !showResult && stepSubmitted && !stepAnswer?.is_correct && !exercisesResult.isLoading;
  const showSubmitButton =
    !showResult && !error && !showLimitError && !exercisesResult.isLoading;
  const hasFooter =
    !props.smallVocab &&
    (showResult || showLimitError || showAnswerToggle || showSubmitButton);

  return (
    <Dialog
      className="ai-generated-dialog"
      size="lg"
      open={props.open}
      closeOnOverlayClick={false}
      onClose={props.onClose}
      title={t("Learn.vocabExerciseDialog.practiceMyVocab")}
      secondaryAction={
        hasFooter &&
        (showResult && !showLimitError ? (
          <GhostButton className="practice-more" onClick={handlePracticeMore}>
            {t("Dictionary.exerciseDialog.practiceMore")}
          </GhostButton>
        ) : showAnswerToggle ? (
          <GhostButton className="show-answers" onClick={handleToggleShowAnswer}>
            {!showAnswer ? <VisibilityIcon /> : <VisibilityOffIcon />}{" "}
            {!showAnswer
              ? t("Learn.vocabExerciseDialog.showAnswers")
              : t("Learn.vocabExerciseDialog.hideAnswers")}
          </GhostButton>
        ) : null)
      }
      mainAction={
        hasFooter &&
        (showResult || showLimitError ? (
          <Button size="small" onClick={props.onClose}>
            {t("Common.actions.done")}
          </Button>
        ) : showSubmitButton ? (
          <Button size="small" onClick={handleSubmitAnswer}>
            {!stepSubmitted
              ? t("Learn.vocabExerciseDialog.showAnswers")
              : step === exerciseCount - 1
                ? t("Learn.vocabExerciseDialog.showFinalScore")
                : t("Learn.vocabExerciseDialog.nextQuestion")}
            {stepSubmitted && <ArrowRightIcon />}
          </Button>
        ) : null)
      }
    >
      <ApiErrorBanner error={error} />
      {!props.smallVocab && !exercisesResult.isLoading && !showLimitError && (
        <div className="ai-quiz-counter">
          {t("Learn.vocabExerciseDialog.question")} {step + 1} / {exercises?.length}
        </div>
      )}

      {showLimitError && (
        <VStack align="center" className="limit-error">
          <ForbiddenImg />
          <h3>{t("Dictionary.examplesDialog.dailyLimitTitle")}</h3>
          <p>{t("Dictionary.examplesDialog.dailyLimitHint")}</p>
          <hr />
        </VStack>
      )}

      {!error && !showLimitError && props.smallVocab && (
        <div className="ai-generated-less-vocab-warning">
          {t("Learn.vocabExerciseDialog.notEnoughWords")}
        </div>
      )}

      {!props.smallVocab && !error && !showLimitError && (
        <div className="ai-generated-dialog-content">
          {showResult && (
            <div className="result-progress">
              <ProgressRing
                size={284}
                progress={percentage}
                strokeWidth={6}
                // Token hexes (SVG attributes can't resolve CSS vars):
                // pass = --action-brand teal, neutral = --border-control grey.
                pathColor={passed ? "var(--action-brand)" : "var(--border-control)"}
                trailColor="var(--c-slate-100)"
                arcAngle={240}
                rotation={-120}
                pointerRadius={12}
              />
              <div className={`progress-content ${passed ? "passed" : "neutral"}`}>
                {passed && <CheckIcon />}
                {t("Dictionary.exerciseDialog.yourScore")} {percentage}%
              </div>
            </div>
          )}

          {!showResult && (
            <>
              {exercisesResult.isLoading ? (
                <div className="spinner">
                  <TalaeraSpinner />
                </div>
              ) : (
                <div className="example-sentence">
                  <span>{step + 1}.</span>
                  {exercises?.[step] && (
                    <>
                      <span>{exercises[step].sentence.split("___")[0]}</span>

                      {stepSubmitted ? (
                        <span
                          className={`answer-result ${showAnswer || stepAnswer?.is_correct ? "correct" : "wrong"}`}
                        >
                          {showAnswer ? (
                            <>
                              <span>{stepAnswer?.word}</span>
                              <span>{correctAnswer?.word}</span>
                            </>
                          ) : (
                            stepAnswer?.word
                          )}
                          {(showAnswer || stepAnswer?.is_correct) && <CorrectIcon />}
                          {!showAnswer && !stepAnswer?.is_correct && <InCorrectIcon />}
                        </span>
                      ) : (
                        <Dropdown
                          value={stepAnswers[step]?.value}
                          onChange={handleChangeAnswer}
                        >
                          <option />
                          {exercises[step].answers.map((answerItem, index) => {
                            return (
                              <option key={index} value={answerItem.id}>
                                {answerItem.word}
                              </option>
                            );
                          })}
                        </Dropdown>
                      )}
                      <span>{exercises[step].sentence.split("___")[1]}</span>
                    </>
                  )}

                  {stepSubmitted && stepAnswer && (
                    <div
                      className={`result-explanation ${stepAnswer.is_correct ? "correct" : "wrong"}`}
                    >
                      <div className="result-explanation-title">
                        {stepAnswer.is_correct ? (
                          <CheckOutlineIcon />
                        ) : (
                          <ErrorOutlineIcon />
                        )}
                        {t("Learn.vocabExerciseDialog.answerKey")}
                      </div>
                      {translatedText ? translatedText : stepAnswer.explanation}
                      <Button
                        variant="plain"
                        className="translate-icon"
                        Icon={TranslateIcon}
                        aria-label={t("Common.actions.translate")}
                        onClick={handleClickTranslate}
                      />
                      {!translatedText && !user.profile.native_language && (
                        <LanguageSelect
                          value={targetLang}
                          onChange={handleChangeTargetLang}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Dialog>
  );
};
